import { FC } from "react";
import { useTermsModalContext } from "../../../context/TermsModalContext";
import { useAppSelector } from "../../../hooks/storeHooks";
import { TermsViewerModeEnum } from "../../../types/termsTypes";
import TermsBlockingModal from "./TermsBlockingModal";
import TermsBlockedRedirect from "./TermsBlockedRedirect";
import TermsDocumentViewer from "./TermsDocumentViewer";

/**
 * The single mount point for the T&Cs surfaces. Lives inside `DashboardLayout`,
 * so it covers every authenticated page including the post-login landing.
 *
 * The notice banner is rendered by the layout itself rather than here, because
 * it occupies layout space instead of floating over it.
 */
const TermsGate: FC = () => {
	const {
		showBlockingModal,
		viewerMode,
		openBlockingViewer,
		closeViewer,
		acceptTerms,
		remindSuperAdmin,
		logout,
	} = useTermsModalContext();

	const {
		current,
		content,
		isLoadingContent,
		contentError,
		viewedVersionEffectiveDate,
		isAccepting,
		acceptError,
		cooldownExpiresAt,
		reminderRecipientName,
		isReminding,
		reminderError,
	} = useAppSelector((state) => state.terms);

	// Per vendor, not per team member: a colleague's reminder disables the button
	// for everyone until the shared expiry passes.
	const isReminderOnCooldown = !!cooldownExpiresAt && new Date(cooldownExpiresAt) > new Date();

	const isViewerOpen = viewerMode !== null;

	return (
		<>
			<TermsBlockedRedirect />

			<TermsBlockingModal
				// Suppressed while the viewer is up so the two never stack.
				open={showBlockingModal && !isViewerOpen}
				canAccept={!!current?.canAccept}
				isNewVendor={current?.acceptedVersion === null}
				keyChanges={current?.keyChanges ?? []}
				isReminderOnCooldown={isReminderOnCooldown}
				isReminding={isReminding}
				reminderRecipientName={reminderRecipientName}
				reminderError={reminderError}
				onReviewTerms={openBlockingViewer}
				onRemindSuperAdmin={remindSuperAdmin}
				onLogout={logout}
			/>

			{isViewerOpen && (
				<TermsDocumentViewer
					open
					mode={viewerMode}
					content={content}
					isLoading={isLoadingContent}
					error={contentError}
					effectiveDate={
						viewerMode === TermsViewerModeEnum.READ_ONLY
							? (viewedVersionEffectiveDate ?? undefined)
							: current?.effectiveDate
					}
					canAccept={!!current?.canAccept}
					role={current?.role ?? null}
					isAccepting={isAccepting}
					acceptError={acceptError}
					isReminderOnCooldown={isReminderOnCooldown}
					isReminding={isReminding}
					reminderRecipientName={reminderRecipientName}
					onClose={closeViewer}
					onAccept={acceptTerms}
					onRemindSuperAdmin={remindSuperAdmin}
					onLogout={logout}
				/>
			)}
		</>
	);
};

export default TermsGate;
